import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useJudicialTriggerStore } from "../store/judicialTriggerStore";
import { useAIContext } from "../store/aiContextStore";
import { useKeyboardShortcuts } from "../hooks/useKeyboardShortcuts";
import { importJSON } from "../utils/ImportJSON";

/**
 * Judicial Trigger Engine UI component.
 * Allows users to input risk scores and compute judicial action outcomes.
 */
export default function JudicialTrigger() {
  const { t } = useTranslation();
  const store = useJudicialTriggerStore();
  const [conflictScore, setConflictScore] = useState(store.conflictScore);
  const [oligarchClusterScore, setOligarchClusterScore] = useState(store.oligarchClusterScore);
  const [procurementRiskScore, setProcurementRiskScore] = useState(store.procurementRiskScore);
  const [tribunalDecision, setTribunalDecision] = useState(store.tribunalDecision);
  const [integrityRisk, setIntegrityRisk] = useState(store.integrityRisk);
  const [evidenceAdmissibility, setEvidenceAdmissibility] = useState(store.evidenceAdmissibility);
  const [geopoliticalWeight, setGeopoliticalWeight] = useState(store.geopoliticalWeight);

  // Set active panel
  useEffect(() => {
    useAIContext.getState().setActivePanel("JudicialTriggerPanel");
  }, []);

  // Update store when inputs change (avoid including store in deps to prevent loops)
  useEffect(() => {
    store.setConflictScore(conflictScore);
    store.setOligarchClusterScore(oligarchClusterScore);
    store.setProcurementRiskScore(procurementRiskScore);
    store.setTribunalDecision(tribunalDecision);
    store.setIntegrityRisk(integrityRisk);
    store.setEvidenceAdmissibility(evidenceAdmissibility);
    store.setGeopoliticalWeight(geopoliticalWeight);
    // Auto-compute on input change
    store.computeJudicialOutcome();
  }, [conflictScore, oligarchClusterScore, procurementRiskScore, tribunalDecision, integrityRisk, evidenceAdmissibility, geopoliticalWeight]);

  const handleCompute = async () => {
    await store.computeJudicialOutcome();
  };

  const handleExport = () => {
    store.exportJudicialTrigger();
  };

  const handleImport = async () => {
    try {
      const data = await importJSON<{
        conflictScore?: number;
        oligarchClusterScore?: number;
        procurementRiskScore?: number;
        tribunalDecision?: "pending" | "warning" | "fine" | "disqualification";
        integrityRisk?: number;
        evidenceAdmissibility?: "low" | "medium" | "high";
        geopoliticalWeight?: number;
      }>();
      if (data) {
        store.importJudicialTrigger(data);
      }
    } catch (error) {
      console.error("Import failed:", error);
    }
  };

  // Keyboard shortcuts (after function definitions)
  useKeyboardShortcuts([
    { key: 'e', ctrl: true, action: handleExport, description: 'Export judicial trigger' },
    { key: 'i', ctrl: true, action: handleImport, description: 'Import judicial trigger' },
    { key: 'c', ctrl: true, action: handleCompute, description: 'Compute judicial outcome' },
  ]);

  return (
    <div className="space-y-6 font-orbitron bg-black text-neutral-100">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Input Section */}
        <div className="space-y-4 p-6 border border-evat-gold/40 rounded-lg bg-black/50">
          <h2 className="text-xl font-orbitron text-evat-neon border-b border-evat-gold/40 pb-2">
            {t("judicial.inputs") || "Input Parameters"}
          </h2>

          {/* Conflict Score */}
          <div>
            <label className="block text-sm text-neutral-300 mb-2">
              {t("judicial.conflictScore") || "Conflict Score"} ({conflictScore})
            </label>
            <input
              type="range"
              min="0"
              max="100"
              value={conflictScore}
              onChange={(e) => setConflictScore(Number(e.target.value))}
              className="w-full"
            />
          </div>

          {/* Oligarch Cluster Score */}
          <div>
            <label className="block text-sm text-neutral-300 mb-2">
              {t("judicial.oligarchClusterScore") || "Oligarch Cluster Score"} ({oligarchClusterScore})
            </label>
            <input
              type="range"
              min="0"
              max="100"
              value={oligarchClusterScore}
              onChange={(e) => setOligarchClusterScore(Number(e.target.value))}
              className="w-full"
            />
          </div>

          {/* Procurement Risk Score */}
          <div>
            <label className="block text-sm text-neutral-300 mb-2">
              {t("judicial.procurementRiskScore") || "Procurement Risk Score"} ({procurementRiskScore})
            </label>
            <input
              type="range"
              min="0"
              max="100"
              value={procurementRiskScore}
              onChange={(e) => setProcurementRiskScore(Number(e.target.value))}
              className="w-full"
            />
          </div>

          {/* Tribunal Decision */}
          <div>
            <label className="block text-sm text-neutral-300 mb-2">
              {t("judicial.tribunalDecision") || "Tribunal Decision"}
            </label>
            <select
              value={tribunalDecision}
              onChange={(e) => setTribunalDecision(e.target.value as any)}
              className="w-full bg-black border border-evat-gold/40 text-evat-neon rounded px-3 py-2 focus:outline-none focus:border-evat-gold"
            >
              <option value="pending">{t("judicial.pending") || "Pending"}</option>
              <option value="warning">{t("judicial.warning") || "Warning"}</option>
              <option value="fine">{t("judicial.fine") || "Fine"}</option>
              <option value="disqualification">{t("judicial.disqualification") || "Disqualification"}</option>
            </select>
          </div>

          {/* Integrity Risk */}
          <div>
            <label className="block text-sm text-neutral-300 mb-2">
              {t("judicial.integrityRisk") || "Integrity Risk"} ({integrityRisk})
            </label>
            <input
              type="range"
              min="0"
              max="100"
              value={integrityRisk}
              onChange={(e) => setIntegrityRisk(Number(e.target.value))}
              className="w-full"
            />
          </div>

          {/* Evidence Admissibility */}
          <div>
            <label className="block text-sm text-neutral-300 mb-2">
              {t("judicial.evidenceAdmissibility") || "Evidence Admissibility"}
            </label>
            <select
              value={evidenceAdmissibility}
              onChange={(e) => setEvidenceAdmissibility(e.target.value as any)}
              className="w-full bg-black border border-evat-gold/40 text-evat-neon rounded px-3 py-2 focus:outline-none focus:border-evat-gold"
            >
              <option value="low">{t("judicial.low") || "Low"}</option>
              <option value="medium">{t("judicial.medium") || "Medium"}</option>
              <option value="high">{t("judicial.high") || "High"}</option>
            </select>
          </div>

          {/* Geopolitical Weight */}
          <div>
            <label className="block text-sm text-neutral-300 mb-2">
              {t("judicial.geopoliticalWeight") || "Geopolitical Weight"} ({geopoliticalWeight})
            </label>
            <input
              type="range"
              min="0"
              max="5"
              step="0.1"
              value={geopoliticalWeight}
              onChange={(e) => setGeopoliticalWeight(Number(e.target.value))}
              className="w-full"
            />
          </div>

          {/* Action Buttons */}
          <div className="flex gap-4 pt-4">
            <button
              onClick={handleCompute}
              className="flex-1 bg-evat-neon text-black px-6 py-3 rounded font-orbitron hover:shadow-neon transition-all"
            >
              {t("judicial.compute") || "Compute Judicial Action"}
            </button>
            <button
              onClick={handleImport}
              className="flex-1 bg-emerald-700/20 border border-emerald-700 text-emerald-400 px-6 py-3 rounded font-orbitron hover:bg-emerald-700/30 transition-all"
            >
              {t("judicial.import") || "Import Judicial Trigger"}
            </button>
            <button
              onClick={handleExport}
              className="flex-1 bg-evat-gold/20 border border-evat-gold text-evat-gold px-6 py-3 rounded font-orbitron hover:bg-evat-gold/30 transition-all"
            >
              {t("judicial.export") || "Export Judicial Trigger"}
            </button>
          </div>
        </div>

        {/* Results Section */}
        <div className="space-y-4 p-6 border border-evat-gold/40 rounded-lg bg-black/50">
          <h2 className="text-xl font-orbitron text-evat-neon border-b border-evat-gold/40 pb-2">
            {t("judicial.results") || "Judicial Outcome"}
          </h2>

          {/* Judicial Action */}
          <div className="p-4 border border-evat-neon/40 rounded bg-black/30">
            <div className="text-sm text-neutral-400 mb-1">
              {t("judicial.judicialAction") || "Judicial Action"}
            </div>
            <div className={`text-2xl font-orbitron ${
              store.judicialAction === "EU-Prosecution" ? "text-red-400" :
              store.judicialAction === "NationalInvestigation" ? "text-orange-400" :
              store.judicialAction === "RegionalInvestigation" ? "text-yellow-400" :
              "text-green-400"
            }`}>
              {store.judicialAction}
            </div>
          </div>

          {/* Trigger Severity */}
          <div className="p-4 border border-evat-neon/40 rounded bg-black/30">
            <div className="text-sm text-neutral-400 mb-1">
              {t("judicial.triggerSeverity") || "Trigger Severity"}
            </div>
            <div className={`text-2xl font-orbitron ${
              store.triggerSeverity >= 75 ? "text-red-400" :
              store.triggerSeverity >= 60 ? "text-orange-400" :
              store.triggerSeverity >= 40 ? "text-yellow-400" :
              "text-green-400"
            }`}>
              {store.triggerSeverity.toFixed(1)}%
            </div>
          </div>

          {/* Recommended Auditor Tier */}
          <div className="p-4 border border-evat-neon/40 rounded bg-black/30">
            <div className="text-sm text-neutral-400 mb-1">
              {t("judicial.recommendedAuditorTier") || "Recommended Auditor Tier"}
            </div>
            <div className="text-2xl font-orbitron text-evat-gold">
              Tier {store.recommendedAuditorTier}
            </div>
          </div>

          {/* Evidence Admissibility */}
          <div className="p-4 border border-evat-neon/40 rounded bg-black/30">
            <div className="text-sm text-neutral-400 mb-1">
              {t("judicial.evidenceAdmissibility") || "Evidence Admissibility"}
            </div>
            <div className={`text-lg font-orbitron ${
              store.evidenceAdmissibility === "high" ? "text-green-400" :
              store.evidenceAdmissibility === "medium" ? "text-yellow-400" :
              "text-red-400"
            }`}>
              {store.evidenceAdmissibility.toUpperCase()}
            </div>
          </div>

          {/* Tribunal Decision */}
          <div className="p-4 border border-evat-neon/40 rounded bg-black/30">
            <div className="text-sm text-neutral-400 mb-1">
              {t("judicial.tribunalDecision") || "Tribunal Decision"}
            </div>
            <div className={`text-lg font-orbitron ${
              store.tribunalDecision === "disqualification" ? "text-red-400" :
              store.tribunalDecision === "fine" ? "text-orange-400" :
              store.tribunalDecision === "warning" ? "text-yellow-400" :
              "text-blue-400"
            }`}>
              {store.tribunalDecision.toUpperCase()}
            </div>
          </div>

          {/* Geopolitical Weight */}
          <div className="p-4 border border-evat-neon/40 rounded bg-black/30">
            <div className="text-sm text-neutral-400 mb-1">
              {t("judicial.geopoliticalWeight") || "Geopolitical Weight"}
            </div>
            <div className="text-lg font-orbitron text-evat-neon">
              {store.geopoliticalWeight.toFixed(1)} / 5.0
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

